import { Injectable, Inject, ElementRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class VideoThumbnailService {
  // Some video blobs (e.g. duration === Infinity) never fire 'seeked', which would hang the caller forever.
  private static readonly SEEK_TIMEOUT_MS = 8000;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  public generateThumbnail(videoFile: Blob, duration = 0.15, size?: { width?: number; height?: number }): Promise<string> {
    const video: HTMLVideoElement = this.document.createElement('video');
    const canvas: HTMLCanvasElement = this.document.createElement('canvas');
    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');
    let blobUrl: string;

    if (!context) {
      return Promise.reject('Could not get 2D rendering context');
    }

    return new Promise<string>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Timed out generating video thumbnail'));
        cleanup();
      }, VideoThumbnailService.SEEK_TIMEOUT_MS);

      const cleanup = () => {
        clearTimeout(timeoutId);
        // Remove the video and canvas elements from the DOM if they were added
        if (video.parentNode) {
          video.parentNode.removeChild(video);
        }
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
        // Remove event listeners
        video.removeEventListener('error', reject);
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('seeked', onSeeked);
        canvas.removeEventListener('error', reject);
      };

      const onSeeked = () => {
        const sourceWidth = video.videoWidth;
        const sourceHeight = video.videoHeight;
        const targetWidth = size?.width ?? (size?.height ? Math.round((sourceWidth / sourceHeight) * size.height) : sourceWidth);
        const targetHeight = size?.height ?? (size?.width ? Math.round((sourceHeight / sourceWidth) * size.width) : sourceHeight);

        canvas.width = targetWidth;
        canvas.height = targetHeight;
        context.drawImage(video, 0, 0, targetWidth, targetHeight);

        // Use toDataURL() as before
        const dataUrl = canvas.toDataURL();
        resolve(dataUrl);

        // Revoke the Blob URL and clean up all elements
        window.URL.revokeObjectURL(blobUrl);
        cleanup();
      };

      const onLoadedMetadata = () => {
        const thumbnailTime = video.duration * duration;
        video.addEventListener('seeked', onSeeked);
        video.currentTime = thumbnailTime;
      };

      canvas.addEventListener('error', event => {
        reject(event.error);
        cleanup();
      });

      video.addEventListener('error', event => {
        reject(event.error);
        cleanup();
      });

      video.addEventListener('loadedmetadata', onLoadedMetadata);

      blobUrl = window.URL.createObjectURL(videoFile);
      video.preload = 'auto';
      video.src = blobUrl;
      video.load();
    });
  }

  public generateThumbnailFromVideoUrl(
    videoElementRef: ElementRef<HTMLVideoElement>,
    canvasElementRef: ElementRef<HTMLCanvasElement>,
    duration = 0.15,
    size?: { width?: number; height?: number }
  ): Promise<string> {
    const video = videoElementRef.nativeElement;
    if (!video.crossOrigin) {
      video.crossOrigin = 'anonymous';
    }
    const canvas = canvasElementRef.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return Promise.reject('Could not get 2D rendering context');
    }

    const toBase64 = (): string => {
      const sourceWidth = video.videoWidth || canvas.width;
      const sourceHeight = video.videoHeight || canvas.height;
      const targetWidth = size?.width ?? (size?.height ? Math.round((sourceWidth / sourceHeight) * size.height) : sourceWidth);
      const targetHeight = size?.height ?? (size?.width ? Math.round((sourceHeight / sourceWidth) * size.width) : sourceHeight);

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

      // Use JPEG for smaller preview; adjust quality if needed
      return canvas.toDataURL('image/jpeg', 0.8);
    };

    return new Promise<string>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        onError(new Error('Timed out generating video thumbnail'));
      }, VideoThumbnailService.SEEK_TIMEOUT_MS);

      const onError = (e: Event | any) => {
        cleanup();
        reject((e && (e as any).error) || e);
      };

      const onSeeked = () => {
        try {
          const base64 = toBase64();
          cleanup();
          resolve(base64);
        } catch (err) {
          onError(err);
        }
      };

      const onLoadedMetadata = () => {
        // Seek to a fraction of the duration (default 15%)
        const targetTime = Math.max(0, Math.min(video.duration * duration, video.duration || 0));
        video.addEventListener('seeked', onSeeked, { once: true });
        try {
          video.currentTime = isFinite(targetTime) ? targetTime : 0;
        } catch {
          // Some browsers require a tiny delay before seeking
          setTimeout(() => (video.currentTime = 0), 0);
        }
      };

      const cleanup = () => {
        clearTimeout(timeoutId);
        video.removeEventListener('error', onError);
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('seeked', onSeeked);
        canvas.removeEventListener('error', onError);
        video.currentTime = 0;
      };

      canvas.addEventListener('error', onError, { once: true });
      video.addEventListener('error', onError, { once: true });

      if (video.readyState >= 1) {
        // Metadata is already available
        onLoadedMetadata();
      } else {
        video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
        // Ensure the browser starts loading metadata
        try {
          video.load();
        } catch {
          console.error('Error loading video');
        }
      }
    });
  }
}
